import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { Profile, Session } from '../domain/entity/auth'
import { getJson, remove, setJson } from '@/modules/shared/utility/localStorage'

const SESSION_KEY = 'session'

/**
 * What actually lives under `SESSION_KEY`. `firstName`/`lastName` ride along
 * with the session — unlike the rest of the profile — because they are a
 * display name, not a permission: showing one before `/auth/me` answers is
 * safe, so it is worth surviving a page reload. Optional because sessions
 * persisted before this existed have neither field.
 */
interface PersistedSession extends Session {
  firstName?: string | null
  lastName?: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const session = getJson<PersistedSession>(SESSION_KEY)

  const token = ref<string | null>(session?.token ?? null)
  const refreshToken = ref<string | null>(session?.refreshToken ?? null)
  const email = ref<string | null>(session?.email ?? null)
  const firstName = ref<string | null>(session?.firstName ?? null)
  const lastName = ref<string | null>(session?.lastName ?? null)

  // The rest of the profile is never persisted: permissions are resolved
  // server-side per request, so the UI re-fetches them on every app load.
  const roles = ref<string[]>([])
  const permissions = ref<string[]>([])
  const profileLoaded = ref(false)
  const isPlatformAdmin = ref(false)
  const mustChangePassword = ref(false)

  const isAuthenticated = computed(() => Boolean(token.value))

  // Neither half alone is a name: "Ana" plus a stray "null" reads worse than
  // showing nothing, and an empty string here is what tells the menu to fall
  // back to the email alone.
  const fullName = computed(() =>
    [firstName.value, lastName.value].filter((part): part is string => Boolean(part)).join(' '),
  )

  function persistSession(): void {
    if (token.value === null || refreshToken.value === null || email.value === null) return
    setJson(SESSION_KEY, {
      token: token.value,
      refreshToken: refreshToken.value,
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
    })
  }

  function setSession(newSession: Session): void {
    token.value = newSession.token
    refreshToken.value = newSession.refreshToken
    email.value = newSession.email
    // A fresh sign-in starts with no name: it arrives later from `/auth/me`,
    // and carrying over whoever was signed in before would show the wrong
    // person's name for the moment before the profile call resolves.
    firstName.value = null
    lastName.value = null
    persistSession()
  }

  // Spent by the http_handler's 401 interceptor after a successful
  // POST /auth/refresh-token. The server rotates the refresh token on every
  // use, so BOTH must be persisted here — persisting only the access token
  // would make the *next* refresh attempt spend an already-consumed refresh
  // token and fail, silently forcing a logout at the second expiry instead
  // of this one.
  function setTokens(newToken: string, newRefreshToken: string): void {
    token.value = newToken
    refreshToken.value = newRefreshToken
    persistSession()
  }

  function setProfile(profile: Profile): void {
    roles.value = profile.roles
    permissions.value = [...profile.permissions]
    isPlatformAdmin.value = profile.isPlatformAdmin
    mustChangePassword.value = profile.mustChangePassword
    profileLoaded.value = true
    firstName.value = profile.firstName
    lastName.value = profile.lastName
    persistSession()
  }

  // Spent by useChangePassword on success: the account cleared its debt on
  // the API, and the guard must stop redirecting to Settings without
  // waiting for a full profile refetch.
  function clearMustChangePassword(): void {
    mustChangePassword.value = false
  }

  function clearSession(): void {
    token.value = null
    refreshToken.value = null
    email.value = null
    firstName.value = null
    lastName.value = null
    roles.value = []
    permissions.value = []
    isPlatformAdmin.value = false
    mustChangePassword.value = false
    profileLoaded.value = false
    remove(SESSION_KEY)
  }

  // Fail closed: until the profile arrives from /auth/me, NOTHING is allowed.
  function hasAllPermissions(required: string[]): boolean {
    return profileLoaded.value && required.every((p) => permissions.value.includes(p))
  }

  return {
    token,
    refreshToken,
    email,
    firstName,
    lastName,
    fullName,
    roles,
    permissions,
    profileLoaded,
    isPlatformAdmin,
    mustChangePassword,
    isAuthenticated,
    setSession,
    setTokens,
    setProfile,
    clearMustChangePassword,
    clearSession,
    hasAllPermissions,
  }
})
